import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const hebrew = (theme: Theme, fontSize: string, fontWeight: number, lineHeight = "140%") => ({
  fontFamily: theme.typography.fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
});

const rtlFullWidth = {
  direction: "rtl" as const,
  textAlign: "right" as const,
  width: "100%",
};

const labelTypography = (theme: Theme) => ({ ...hebrew(theme, "12px", 500, "80%"), textAlign: "right" as const });
const fieldTypography = (theme: Theme) => ({ ...hebrew(theme, "14px", 400), textAlign: "right" as const });

const TABLE_ROW_HEIGHT = 52;
const loadTableColumns = "repeat(4, minmax(0, 1fr))";

const fieldBoxLayout = {
  height: 48,
  gap: 10,
  borderRadius: 4,
  borderWidth: 1,
  paddingTop: 12,
  paddingRight: 16,
  paddingBottom: 12,
  paddingLeft: 8,
} as const;

const metricBoxLayout = {
  height: 38,
  gap: 10,
  borderRadius: 4,
  borderWidth: 1,
  paddingTop: 14,
  paddingRight: 16,
  paddingBottom: 14,
  paddingLeft: 16,
} as const;

const fieldInputPadding = `${fieldBoxLayout.paddingTop}px ${fieldBoxLayout.paddingRight}px ${fieldBoxLayout.paddingBottom}px ${fieldBoxLayout.paddingLeft}px`;

const grid = (columns: number, gap: number = fieldBoxLayout.gap) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: `${gap}px`,
  width: "100%",
  direction: "rtl" as const,
});

const outlinedFieldRoot = (theme: Theme) => ({
  margin: 0,
  height: fieldBoxLayout.height,
  minHeight: fieldBoxLayout.height,
  borderRadius: `${fieldBoxLayout.borderRadius}px`,
  backgroundColor: theme.palette.background.paper,
  boxSizing: "border-box" as const,
  alignItems: "center",
  padding: 0,
  ...fieldTypography(theme),
  color: theme.palette.secondary.main,
  "& fieldset": {
    borderColor: theme.palette.divider,
    borderWidth: `${fieldBoxLayout.borderWidth}px`,
    borderRadius: `${fieldBoxLayout.borderRadius}px`,
  },
});

const outlinedFieldInput = (theme: Theme) => ({
  padding: fieldInputPadding,
  direction: "rtl" as const,
  width: "100%",
  height: fieldBoxLayout.height,
  minHeight: fieldBoxLayout.height,
  boxSizing: "border-box" as const,
  ...fieldTypography(theme),
  color: theme.palette.secondary.main,
  "&::placeholder": {
    ...fieldTypography(theme),
    color: theme.palette.text.disabled,
  },
});

const resultRowBase = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "20px",
  height: 42,
  minHeight: 42,
  padding: "14px 16px",
  boxSizing: "border-box" as const,
  direction: "ltr" as const,
};

const tableRowGrid = {
  display: "grid",
  gridTemplateColumns: loadTableColumns,
  height: TABLE_ROW_HEIGHT,
  minHeight: TABLE_ROW_HEIGHT,
  direction: "rtl" as const,
};

const tableCellBase = (theme: Theme) => ({
  padding: "14px 16px",
  height: TABLE_ROW_HEIGHT,
  minHeight: TABLE_ROW_HEIGHT,
  ...hebrew(theme, "12px", 400),
  direction: "rtl" as const,
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box" as const,
});

const actionButtonBase = (theme: Theme) => ({
  height: 43,
  minHeight: 43,
  borderRadius: "4px",
  padding: "10px 16px",
  ...hebrew(theme, "14px", 500),
  textTransform: "none" as const,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
});

export const useStyles = makeStyles((theme: Theme) => {
  const cardShell = {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "4px",
    padding: "16px",
    boxSizing: "border-box" as const,
  };

  return {
    pageRoot: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      direction: "rtl",
      gap: "16px",
    },
    sectionCard: cardShell,
    grid2: grid(2),
    grid3: grid(3),
    grid4: grid(4),
    sectionFields: {
      display: "flex",
      flexDirection: "column",
      gap: `${fieldBoxLayout.gap}px`,
      width: "100%",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: `${fieldBoxLayout.gap}px`,
      minWidth: 0,
      width: "100%",
      direction: "rtl",
      alignItems: "stretch",
    },
    label: {
      ...labelTypography(theme),
      margin: 0,
      padding: 0,
      color: theme.palette.text.primary,
      display: "block",
      boxSizing: "border-box",
      ...rtlFullWidth,
    },
    selectControl: { margin: 0, width: "100%" },
    input: {
      direction: "rtl",
      margin: 0,
      "& .MuiOutlinedInput-root": outlinedFieldRoot(theme),
      "& .MuiOutlinedInput-input": outlinedFieldInput(theme),
    },
    select: {
      direction: "rtl",
      height: fieldBoxLayout.height,
      minHeight: fieldBoxLayout.height,
      borderRadius: `${fieldBoxLayout.borderRadius}px`,
      backgroundColor: theme.palette.background.paper,
      boxSizing: "border-box",
      ...fieldTypography(theme),
      color: theme.palette.secondary.main,
      "& .MuiOutlinedInput-root": {
        ...outlinedFieldRoot(theme),
        height: fieldBoxLayout.height,
        minHeight: fieldBoxLayout.height,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
        borderWidth: `${fieldBoxLayout.borderWidth}px`,
        borderRadius: `${fieldBoxLayout.borderRadius}px`,
      },
      "& .MuiSelect-select": {
        padding: `${fieldBoxLayout.paddingTop}px ${fieldBoxLayout.paddingRight}px ${fieldBoxLayout.paddingBottom}px 32px !important`,
        minHeight: fieldBoxLayout.height,
        height: fieldBoxLayout.height,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        direction: "rtl",
        ...fieldTypography(theme),
        color: theme.palette.secondary.main,
      },
      "& .MuiSelect-icon, & .MuiSelect-iconOpen": {
        color: theme.palette.text.primary,
        fontSize: "18px !important",
        left: "8px !important",
        right: "auto !important",
      },
    },
    selectValue: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      ...fieldTypography(theme),
      color: theme.palette.secondary.main,
      ...rtlFullWidth,
    },
    selectPlaceholder: {
      display: "block",
      ...fieldTypography(theme),
      color: theme.palette.text.disabled,
      ...rtlFullWidth,
    },
    summaryRow: { ...grid(3), gap: "16px" },
    summaryCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
      gap: "4px",
      height: 112,
      minHeight: 112,
      padding: "20px",
      borderRadius: "10px",
      boxSizing: "border-box",
      direction: "rtl",
    },
    summaryCardYellow: { backgroundColor: "#FFF8E8" },
    summaryCardGreen: { backgroundColor: "#EAF8F0" },
    summaryCardBlue: { backgroundColor: "#EEF4FF" },
    summaryTitle: {
      ...hebrew(theme, "14px", 600),
      color: theme.palette.secondary.main,
      ...rtlFullWidth,
    },
    summaryValue: {
      ...hebrew(theme, "32px", 600, "120%"),
      ...rtlFullWidth,
    },
    summaryValueYellow: { color: "#E8A317" },
    summaryValueGreen: { color: "#2E9E5B" },
    summaryValueBlue: { color: "#3B82F6" },
    summaryFooter: {
      ...hebrew(theme, "12px", 400),
      color: theme.palette.text.secondary,
      ...rtlFullWidth,
    },
    diagramSection: {
      ...cardShell,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      direction: "rtl",
      minWidth: 0,
      overflow: "hidden",
    },
    diagramHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: "16px",
      direction: "rtl",
    },
    diagramTitle: {
      ...hebrew(theme, "14px", 600),
      color: theme.palette.secondary.main,
      direction: "rtl",
      flexShrink: 0,
    },
    diagramMetrics: {
      ...hebrew(theme, "14px", 400),
      color: theme.palette.text.primary,
      direction: "rtl",
      flexShrink: 0,
    },
    diagramContent: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 280px",
      alignItems: "start",
      gap: "16px",
      width: "100%",
      minWidth: 0,
      backgroundColor: "#FFFFFF",
      direction: "ltr",
    },
    diagramImageWrapper: {
      position: "relative",
      minWidth: 0,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
      aspectRatio: "834 / 468",
    },
    diagramImage: {
      display: "block",
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      objectFit: "contain",
      objectPosition: "top left",
      backgroundColor: "#FFFFFF",
    },
    resultsPanel: {
      width: 280,
      flexShrink: 0,
      borderRadius: "4px",
      border: `1px solid ${theme.palette.divider}`,
      boxSizing: "border-box",
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
      direction: "rtl",
    },
    resultsHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 52,
      minHeight: 52,
      padding: "14px 0",
      boxSizing: "border-box",
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
      ...hebrew(theme, "14px", 600),
      direction: "rtl",
    },
    resultRow: resultRowBase,
    resultRowDivider: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    resultLabel: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      direction: "ltr",
      ...hebrew(theme, "12px", 500),
      color: theme.palette.text.primary,
      textAlign: "left",
    },
    resultLabelText: { direction: "rtl" },
    resultLabelColon: { direction: "ltr" },
    resultValue: {
      ...hebrew(theme, "12px", 400),
      color: theme.palette.secondary.main,
      textAlign: "right",
      direction: "rtl",
      flexShrink: 0,
    },
    statusButtonWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 71,
      minHeight: 71,
      padding: "14px 16px",
      boxSizing: "border-box",
    },
    statusButton: {
      ...actionButtonBase(theme),
      width: "100%",
      ...hebrew(theme, "14px", 600),
      margin: 0,
    },
    loadTableTitle: {
      ...hebrew(theme, "14px", 600),
      color: theme.palette.secondary.main,
      textAlign: "right",
      direction: "rtl",
      marginBottom: "12px",
    },
    loadTable: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "4px",
      overflow: "hidden",
      direction: "rtl",
    },
    loadTableHeadRow: {
      ...tableRowGrid,
      backgroundColor: theme.palette.grey[400],
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      overflow: "hidden",
    },
    loadTableBodyRow: {
      ...tableRowGrid,
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: "none" },
    },
    loadTableHeadCell: {
      ...tableCellBase(theme),
      fontWeight: 500,
      color: theme.palette.text.primary,
      textAlign: "center",
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
    loadTableBodyCell: {
      ...tableCellBase(theme),
      color: theme.palette.secondary.main,
      textAlign: "right",
      justifyContent: "flex-start",
    },
    loadTableNote: {
      marginTop: "16px",
      ...hebrew(theme, "14px", 400),
      color: theme.palette.text.primary,
      textAlign: "right",
      direction: "rtl",
    },
    loadTableMetrics: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      marginTop: "16px",
      direction: "rtl",
    },
    loadTableMetricField: {
      display: "flex",
      alignItems: "center",
      gap: `${metricBoxLayout.gap}px`,
      direction: "rtl",
      flexShrink: 0,
    },
    loadTableMetricFieldSeparated: {
      borderRight: `1px solid ${theme.palette.divider}`,
      paddingRight: "16px",
      marginRight: "16px",
    },
    loadTableMetricLabel: {
      margin: 0,
      ...hebrew(theme, "12px", 500),
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
    loadTableMetricValueBox: {
      height: metricBoxLayout.height,
      padding: `${metricBoxLayout.paddingTop}px ${metricBoxLayout.paddingRight}px ${metricBoxLayout.paddingBottom}px ${metricBoxLayout.paddingLeft}px`,
      border: `${metricBoxLayout.borderWidth}px solid ${theme.palette.divider}`,
      borderRadius: `${metricBoxLayout.borderRadius}px`,
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.paper,
      flexShrink: 0,
    },
    loadTableMetricValue: {
      margin: 0,
      ...hebrew(theme, "12px", 400, "1"),
      color: theme.palette.secondary.main,
      textAlign: "center",
      whiteSpace: "nowrap",
    },
    loadTableMetricUnit: {
      margin: 0,
      ...hebrew(theme, "12px", 400),
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
    loadTableActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      marginTop: "16px",
      paddingTop: "16px",
      borderTop: `1px solid ${theme.palette.divider}`,
      direction: "rtl",
    },
    loadTableActionsTitle: {
      ...hebrew(theme, "14px", 600),
      color: theme.palette.secondary.main,
      flexShrink: 0,
    },
    loadTableButtons: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: "10px",
      direction: "rtl",
    },
    loadTableOutlinedButton: {
      ...actionButtonBase(theme),
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      "&:hover": { borderColor: theme.palette.primary.main },
    },
    loadTablePrimaryButton: {
      ...actionButtonBase(theme),
      ...hebrew(theme, "14px", 600),
    },
  };
});
